import { AccountGet, Ulid, VatClassGet } from "@kase/my-marathon-communication";
import { computed, signal } from "@preact/signals";
import AppTime from "~/core/AppTime";
import { dateFromApi } from "~/utils";
import { ApiSliceParams } from "../interfaces";
import { createListController } from "../utils";
import { Account, VatClass } from "./interfaces";

export const makeVatClass = ({ connection, company }: ApiSliceParams) => {
    const {
        items: periods,
        listen: listenPeriods,
        whenLoaded: whenPeriodsLoaded,
    } = createListController({
        connection,
        list: (con) => con.company.period.items({ company: company.value }),
        sort: (a, b) => b.end - a.end,
        name: "period",
    });
    const {
        items: vatClasses,
        listen: listenVatClasses,
        loading: loadingVatClasses,
    } = createListController<VatClassGet, VatClass, number>({
        connection,
        list: (con, year) =>
            con.vatClass.items({ company: company.value, year }),
        getKey: (year) => year.toString(),
        name: "vatClass",
    });
    const { items: accounts, listen: listenAccounts } = createListController<
        AccountGet,
        Account,
        number
    >({
        connection,
        list: (con, year) =>
            con.account.items({ company: company.value, year }),
        name: "account",
    });
    const bookKeepYear = signal(0);
    const finished = signal(false);
    const loading = computed(() => {
        const f = finished.value;
        const y = bookKeepYear.value;

        if (f) {
            return false;
        }

        if (!y) {
            return true;
        }

        return loadingVatClasses(y).value;
    });

    const listen = async () => {
        listenPeriods();

        await whenPeriodsLoaded();

        const today = new Date();
        const period =
            periods
                .peek()
                .find(
                    (p) =>
                        dateFromApi(p.start) <= today &&
                        dateFromApi(p.end) >= today,
                ) ?? periods.peek()[0];

        finished.value = true;

        if (!period) {
            return;
        }

        bookKeepYear.value = period.bookkeep_year;

        const cancelVatClasses = listenVatClasses(period.bookkeep_year);
        const cancelAccounts = listenAccounts(period.bookkeep_year);

        AppTime.on("year", () => {
            cancelAccounts();
            cancelVatClasses();
            listen();
        });
    };

    const get = (id: string | null | undefined | Readonly<Ulid>) => {
        if (!id) {
            return null;
        }

        const finalId = typeof id === "string" ? id : id.toString();

        return vatClasses.peek().find((e) => e.id === finalId) ?? null;
    };

    const getByAccount = (accountCode: string | null | undefined) => {
        const account = accounts.value.find((a) => a.code === accountCode);
        const vatClassId = account?.vat_class;

        if (!vatClassId) {
            return null;
        }

        return vatClasses.value.find((v) => v._id.equals(vatClassId)) ?? null;
    };

    const getAccount = (accountCode: string | null | undefined) => {
        if (!accountCode) {
            return null;
        }

        return accounts.value.find((a) => a.code === accountCode) ?? null;
    };

    const getAccountById = (id: string | null | undefined | Readonly<Ulid>) => {
        if (!id) {
            return null;
        }

        const finalId = typeof id === "string" ? id : id.toString();

        return accounts.value.find((a) => a.id === finalId) ?? null;
    };

    return {
        vatClasses,
        loading,
        listen,
        get,
        getByAccount,
        getAccount,
        getAccountById,
    };
};
